import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { getSessionToken } from '../auth/sessionToken';
import {
  createDefaultSnapshot,
  parseLocation,
  snapshotToUrl,
  snapshotsEqual,
  type AppHistorySnapshot,
} from './appHistory';
import { setPendingMessagingPeerUserId } from '../utils/messagingPendingPeer';
import { parsePayplusReturnFromLocation } from '../utils/payplusPayment';
import type { AssistantPriceConstraint } from '../assistant/assistantPriceConstraint';
import type { AssistantSchedule } from '../assistant/assistantSchedule';
import { clearAssistantSearchHits } from '../assistant/assistantSearchHits';
import type { AssetBrowseListSortMode } from '../utils/assetBrowseListSort';

export type AppView =
  | 'home'
  | 'auth'
  | 'connectionTest'
  | 'search'
  | 'assets'
  | 'bookings'
  | 'favorites'
  | 'messages'
  | 'profile';

export type AssetsBrowseParams = {
  /** When set from home type pick, list filters lock to this asset type. Null = all types. */
  assetTypeId: number | null;
  /** Label from the home type strip — used to filter by `asset_type_name` when the catalog API is down. */
  assetTypeLabel: string | null;
  /** When set, the assets tab shows this asset’s detail view. */
  assetId: string | null;
  /** When set, asset detail “back to list” returns to this view instead of the assets list. */
  returnView: AppView | null;
  /**
   * When true, owners use the public catalog browse (`active` / search) instead of
   * list-mine / create. Bottom-nav Assets clears this so owners manage their own assets.
   */
  catalogBrowse: boolean;
  /** Optional name/city keyword for catalog browse list filters (assistant / voice). */
  listSearch?: string | null;
  /** Optional country id for catalog browse list filters (assistant / voice). */
  countryId?: number | null;
  /** Parsed city (Hebrew wire) for assistant chips; Israel list filters use name search. */
  city?: string | null;
  /** Local calendar day `YYYY-MM-DD` from assistant “tomorrow” / weekday. */
  whenDate?: string | null;
  /** Soft month window `YYYY-MM` (“בחודש דצמבר”) — any day that month. */
  whenMonth?: string | null;
  /** Spoken clock time `HH:mm` to prefill booking. */
  whenTime?: string | null;
  /** Display street for the assistant slot chip. */
  street?: string | null;
  /**
   * Spoken generic office / משרד — browse matches lounge, conference room,
   * and other office-like types, not only Private Office.
   */
  officeFamily?: boolean;
  /** Spoken “open 24 hours” — prefer listings with 24h hours or 24/7 amenities. */
  open24h?: boolean;
  /** Spoken amenities — hard facility-icon refine (kitchen / wifi / parking). */
  amenityIcons?: string[] | null;
  /** Spoken hourly/daily price: less / more / equals. */
  price?: AssistantPriceConstraint | null;
  /** Recurring weekly request (assistant browse.schedule). */
  schedule?: AssistantSchedule | null;
  /** Backend assistant-turn ids; filter the catalog to these when present. */
  assetIds?: string[] | null;
  /** List order requested by the assistant (“cheapest” → priceAsc). */
  listSort?: AssetBrowseListSortMode | null;
  /** Assistant “closest to street” — distance sort origin (lat/lng). */
  proximityAnchor?: { lat: number; lng: number } | null;
};

export const defaultAssetsBrowseParams: AssetsBrowseParams = {
  assetTypeId: null,
  assetTypeLabel: null,
  assetId: null,
  returnView: null,
  catalogBrowse: false,
  listSearch: null,
  countryId: null,
  city: null,
  whenDate: null,
  whenMonth: null,
  whenTime: null,
  street: null,
  officeFamily: false,
  open24h: false,
  amenityIcons: null,
  price: null,
  schedule: null,
  assetIds: null,
  listSort: null,
  proximityAnchor: null,
};

export type AuthPane =
  | 'login'
  | 'login-business-owner'
  | 'login-asset-owner'
  | 'register-asset-owner'
  | 'contact'
  | 'register'
  | 'forgot-password';

/** Views guests may open without signing in (browse tabs require auth). */
export const GUEST_ALLOWED_VIEWS: readonly AppView[] = ['auth', 'connectionTest'];

export type OpenBookingsOptions = {
  /** Keep in-flight create booking (e.g. Book from asset list). */
  preserveCreateFlow?: boolean;
  /** Open My Bookings with this reservation's detail view. */
  bookingId?: string | null;
  /** PayPlus browser return outcome. */
  paymentReturn?: 'success' | 'failure' | 'cancel' | null;
};

export type BookingsBrowseParams = {
  /** When set, the bookings tab opens this reservation's detail view. */
  bookingId: string | null;
  /** PayPlus browser return (`/payment/success|failure|cancel`). */
  paymentReturn?: 'success' | 'failure' | 'cancel' | null;
  /** Asset to restore after PayPlus cancel/failure (from `?asset=` or session). */
  returnAssetId?: string | null;
};

export const defaultBookingsBrowseParams: BookingsBrowseParams = {
  bookingId: null,
  paymentReturn: null,
  returnAssetId: null,
};

export type BookingFlowGuard = {
  isActive: boolean;
  confirmLeave: () => boolean;
  abandonFlow: () => void;
};

const inactiveBookingFlowGuard: BookingFlowGuard = {
  isActive: false,
  confirmLeave: () => true,
  abandonFlow: () => {},
};

export type OpenAssetsOptions = {
  /** Skip resetting embedded assets menu (e.g. deep-link with {@link AssetsBrowseParams.assetId}). */
  preserveBrowseState?: boolean;
  /** Replace the current history entry instead of pushing (e.g. cancel booking create). */
  replace?: boolean;
  /** Assistant-driven opens keep the in-memory hit list; every other open browses the catalog. */
  keepAssistantHits?: boolean;
};

export type SetViewOptions = {
  replace?: boolean;
};

/** Nonce lets the profile page re-open the same hub row on repeat presses. */
export type ProfileHubRowRequest = {
  rowId: string;
  nonce: number;
};

type AppNavigationValue = {
  view: AppView;
  setView: (view: AppView, options?: SetViewOptions) => void;
  authPane: AuthPane;
  openAuth: (pane?: AuthPane) => void;
  assetsBrowseParams: AssetsBrowseParams;
  bookingsBrowseParams: BookingsBrowseParams;
  /** Bumps when the assets tab is selected from the bottom bar (reset list view). */
  assetsTabFocus: number;
  /** Bumps when the bookings tab is selected from the bottom bar (reset list view). */
  bookingsTabFocus: number;
  /** Bumps when the favorites tab is selected from the bottom bar (reset list view). */
  favoritesTabFocus: number;
  /** Bumps when the messages tab is selected from the bottom bar (reset inbox view). */
  messagesTabFocus: number;
  /** Bumps when the home tab is selected from the bottom bar (scroll to top). */
  homeTabFocus: number;
  /** Bumps when the profile tab is selected from the bottom bar (reset to profile view). */
  profileTabFocus: number;
  /** Returns false when the user chooses to stay in an in-flight booking flow. */
  requestBookingFlowLeave: () => boolean;
  registerBookingFlowGuard: (guard: BookingFlowGuard | null) => void;
  /** Bottom bar navigation — resets each tab to its root view after optional leave confirm. */
  navigateFromBottomBar: (target: AppView) => boolean;
  openAssets: (params?: Partial<AssetsBrowseParams>, options?: OpenAssetsOptions) => void;
  /** Opens or deep-links an asset detail inside the assets tab (updates browser history). */
  showAssetInBrowse: (assetId: string, params?: Partial<AssetsBrowseParams>) => void;
  clearAssetsBrowseAssetId: () => void;
  clearAssetsBrowseReturnView: () => void;
  openBookings: (options?: OpenBookingsOptions) => void;
  clearBookingsBrowseBookingId: () => void;
  openFavorites: () => void;
  /** Open messages inbox; optionally start/open a 1:1 thread with peerUserId. */
  openMessages: (peerUserId?: string | null) => void;
  openSearch: (initialKeyword?: string) => void;
  /** Sync keyword into history while on the search tab (debounced in {@link AssetSearchView}). */
  updateSearchQuery: (query: string | null) => void;
  searchQuery: string | null;
  openProfile: () => void;
  openProfileHubRow: (rowId: string) => void;
  profileHubRowRequest: ProfileHubRowRequest | null;
  openConnectionTest: () => void;
  /** Browser / in-app back — uses the history stack. */
  navigationBack: () => void;
};

const AppNavigationContext = createContext<AppNavigationValue | null>(null);

function isLoggedInNow(): boolean {
  return Boolean(getSessionToken());
}

function browseParamsForView(
  view: AppView,
  current: AssetsBrowseParams,
  patch?: Partial<AssetsBrowseParams>,
): AssetsBrowseParams {
  if (view !== 'assets') {
    return { ...defaultAssetsBrowseParams };
  }
  return { ...defaultAssetsBrowseParams, ...current, ...patch };
}

function nextSnapshot(
  prev: AppHistorySnapshot,
  patch: {
    view: AppView;
    authPane?: AuthPane;
    assetsBrowseParams?: AssetsBrowseParams;
    searchQuery?: string | null;
  },
): AppHistorySnapshot {
  return {
    view: patch.view,
    authPane: patch.authPane ?? prev.authPane,
    assetsBrowseParams: patch.assetsBrowseParams ?? prev.assetsBrowseParams,
    searchQuery: patch.searchQuery !== undefined ? patch.searchQuery : prev.searchQuery,
  };
}

export function AppNavigationProvider({ children }: { children: ReactNode }) {
  const initial = parseLocation(
    typeof window !== 'undefined' ? window.location.pathname : '/',
    typeof window !== 'undefined' ? window.location.search : '',
    isLoggedInNow(),
  );
  const initialPayplusReturn =
    typeof window !== 'undefined'
      ? parsePayplusReturnFromLocation(window.location.pathname, window.location.search)
      : null;

  const [view, setViewState] = useState<AppView>(initial.view);
  const [authPane, setAuthPane] = useState<AuthPane>(initial.authPane);
  const [assetsBrowseParams, setAssetsBrowseParams] = useState<AssetsBrowseParams>(
    initial.assetsBrowseParams,
  );
  const [bookingsBrowseParams, setBookingsBrowseParams] = useState<BookingsBrowseParams>(() =>
    initialPayplusReturn
      ? {
          bookingId: initialPayplusReturn.bookingId,
          paymentReturn: initialPayplusReturn.outcome,
          returnAssetId: initialPayplusReturn.returnAssetId,
        }
      : defaultBookingsBrowseParams,
  );
  const [assetsTabFocus, setAssetsTabFocus] = useState(0);
  const [bookingsTabFocus, setBookingsTabFocus] = useState(0);
  const [favoritesTabFocus, setFavoritesTabFocus] = useState(0);
  const [messagesTabFocus, setMessagesTabFocus] = useState(0);
  const [homeTabFocus, setHomeTabFocus] = useState(0);
  const [profileTabFocus, setProfileTabFocus] = useState(0);
  const [searchQuery, setSearchQuery] = useState<string | null>(initial.searchQuery);
  const [profileHubRowRequest, setProfileHubRowRequest] = useState<ProfileHubRowRequest | null>(
    null,
  );

  const bookingFlowGuardRef = useRef<BookingFlowGuard>(inactiveBookingFlowGuard);
  const snapshotRef = useRef<AppHistorySnapshot>(initial);
  const isHistoryPopRef = useRef(false);
  const historyReadyRef = useRef(false);

  const applySnapshot = useCallback((snapshot: AppHistorySnapshot) => {
    setViewState(snapshot.view);
    setAuthPane(snapshot.authPane);
    setAssetsBrowseParams(snapshot.assetsBrowseParams);
    setSearchQuery(snapshot.searchQuery);
    snapshotRef.current = snapshot;
  }, []);

  const writeHistory = useCallback((snapshot: AppHistorySnapshot, mode: 'push' | 'replace') => {
    if (typeof window === 'undefined') {
      return;
    }
    const url = snapshotToUrl(snapshot);
    if (mode === 'replace') {
      window.history.replaceState(snapshot, '', url);
    } else {
      window.history.pushState(snapshot, '', url);
    }
    snapshotRef.current = snapshot;
  }, []);

  const navigateTo = useCallback(
    (snapshot: AppHistorySnapshot, options?: { replace?: boolean }) => {
      if (isHistoryPopRef.current || typeof window === 'undefined') {
        applySnapshot(snapshot);
        return;
      }
      const prev = snapshotRef.current;
      const mode = options?.replace ? 'replace' : 'push';
      if (mode === 'push' && snapshotsEqual(prev, snapshot)) {
        applySnapshot(snapshot);
        return;
      }
      applySnapshot(snapshot);
      writeHistory(snapshot, mode);
    },
    [applySnapshot, writeHistory],
  );

  useEffect(() => {
    if (typeof window === 'undefined' || historyReadyRef.current) {
      return;
    }
    historyReadyRef.current = true;
    const snapshot = parseLocation(
      window.location.pathname,
      window.location.search,
      isLoggedInNow(),
    );
    const payplusReturn = parsePayplusReturnFromLocation(
      window.location.pathname,
      window.location.search,
    );
    if (payplusReturn) {
      setBookingsBrowseParams({
        bookingId: payplusReturn.bookingId,
        paymentReturn: payplusReturn.outcome,
        returnAssetId: payplusReturn.returnAssetId,
      });
    }
    applySnapshot(snapshot);
    // Normalize address bar to /bookings after PayPlus return (keep in-memory payment state).
    const historySnapshot =
      payplusReturn && snapshot.view === 'bookings'
        ? nextSnapshot(snapshot, { view: 'bookings' })
        : snapshot;
    writeHistory(historySnapshot, 'replace');
  }, [applySnapshot, writeHistory]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const onPopState = (event: PopStateEvent) => {
      const guard = bookingFlowGuardRef.current;
      if (guard.isActive && !guard.confirmLeave()) {
        writeHistory(snapshotRef.current, 'push');
        return;
      }
      if (guard.isActive) {
        guard.abandonFlow();
      }
      isHistoryPopRef.current = true;
      const snapshot =
        event.state &&
        typeof event.state === 'object' &&
        event.state !== null &&
        'view' in (event.state as AppHistorySnapshot)
          ? (event.state as AppHistorySnapshot)
          : parseLocation(window.location.pathname, window.location.search, isLoggedInNow());
      applySnapshot(snapshot);
      isHistoryPopRef.current = false;
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [applySnapshot, writeHistory]);

  const setView = useCallback(
    (next: AppView, options?: SetViewOptions) => {
      navigateTo(
        nextSnapshot(snapshotRef.current, {
          view: next,
          authPane,
          assetsBrowseParams: browseParamsForView(next, assetsBrowseParams),
        }),
        { replace: options?.replace },
      );
    },
    [navigateTo, authPane, assetsBrowseParams],
  );

  const openAuth = useCallback(
    (pane: AuthPane = 'login') => {
      navigateTo(
        nextSnapshot(snapshotRef.current, {
          view: 'auth',
          authPane: pane,
          assetsBrowseParams: { ...defaultAssetsBrowseParams },
          searchQuery: null,
        }),
      );
    },
    [navigateTo],
  );

  const openAssets = useCallback(
    (params?: Partial<AssetsBrowseParams>, options?: OpenAssetsOptions) => {
      // List / category opens leave detail unless a concrete assetId is provided.
      const patch: Partial<AssetsBrowseParams> = {
        listSearch: null,
        countryId: null,
        city: null,
        whenDate: null,
        whenMonth: null,
        whenTime: null,
        street: null,
        officeFamily: false,
        open24h: false,
        amenityIcons: null,
        price: null,
        schedule: null,
        assetIds: null,
        listSort: null,
        proximityAnchor: null,
        ...params,
        ...(params?.assetId ? {} : { assetId: null }),
      };
      if (!options?.keepAssistantHits) {
        clearAssistantSearchHits();
      }
      const nextBrowse = browseParamsForView('assets', assetsBrowseParams, patch);
      navigateTo(
        nextSnapshot(snapshotRef.current, {
          view: 'assets',
          authPane,
          assetsBrowseParams: nextBrowse,
        }),
        { replace: options?.replace },
      );
      if (!options?.preserveBrowseState && !params?.assetId) {
        setAssetsTabFocus((n) => n + 1);
      }
    },
    [navigateTo, authPane, assetsBrowseParams],
  );

  const showAssetInBrowse = useCallback(
    (assetId: string, params?: Partial<AssetsBrowseParams>) => {
      const id = assetId.trim();
      if (!id) {
        return;
      }
      navigateTo(
        nextSnapshot(snapshotRef.current, {
          view: 'assets',
          authPane,
          assetsBrowseParams: browseParamsForView('assets', assetsBrowseParams, {
            ...params,
            assetId: id,
          }),
        }),
      );
    },
    [navigateTo, authPane, assetsBrowseParams],
  );

  const navigationBack = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.history.back();
    }
  }, []);

  const clearAssetsBrowseAssetId = useCallback(() => {
    if (assetsBrowseParams.assetId) {
      navigationBack();
      return;
    }
    setAssetsBrowseParams((prev) => ({ ...prev, assetId: null }));
  }, [assetsBrowseParams.assetId, navigationBack]);

  const clearAssetsBrowseReturnView = useCallback(() => {
    const snapshot = snapshotRef.current;
      navigateTo(
        nextSnapshot(snapshot, {
          view: snapshot.view,
          assetsBrowseParams: {
            ...snapshot.assetsBrowseParams,
            assetId: null,
            returnView: null,
          },
        }),
        { replace: true },
      );
  }, [navigateTo]);

  const openBookings = useCallback(
    (options?: OpenBookingsOptions) => {
      const nextBookingId =
        options?.bookingId === undefined ? null : options.bookingId?.trim() || null;
      const nextPaymentReturn =
        options?.paymentReturn === undefined ? null : options.paymentReturn;
      setBookingsBrowseParams({
        bookingId: nextBookingId,
        paymentReturn: nextPaymentReturn,
        returnAssetId: null,
      });
      // Keep browse filters in history state during asset→book create so cancel/replace
      // can restore the gym list context (URL stays /bookings).
      const nextAssetsBrowse = options?.preserveCreateFlow
        ? { ...snapshotRef.current.assetsBrowseParams }
        : { ...defaultAssetsBrowseParams };
      navigateTo(
        nextSnapshot(snapshotRef.current, {
          view: 'bookings',
          authPane,
          assetsBrowseParams: nextAssetsBrowse,
        }),
      );
      if (nextBookingId || nextPaymentReturn) {
        return;
      }
      if (!options?.preserveCreateFlow) {
        setBookingsTabFocus((n) => n + 1);
      }
    },
    [navigateTo, authPane],
  );

  const clearBookingsBrowseBookingId = useCallback(() => {
    setBookingsBrowseParams({ bookingId: null, paymentReturn: null, returnAssetId: null });
  }, []);

  const openFavorites = useCallback(() => {
    navigateTo(
      nextSnapshot(snapshotRef.current, {
        view: 'favorites',
        authPane,
        assetsBrowseParams: { ...defaultAssetsBrowseParams },
      }),
    );
    setFavoritesTabFocus((n) => n + 1);
  }, [navigateTo, authPane]);

  const openMessages = useCallback(
    (peerUserId?: string | null) => {
      if (!isLoggedInNow()) {
        navigateTo(
          nextSnapshot(snapshotRef.current, {
            view: 'auth',
            authPane: 'login',
            assetsBrowseParams: { ...defaultAssetsBrowseParams },
            searchQuery: null,
          }),
        );
        return;
      }
      if (peerUserId?.trim()) {
        setPendingMessagingPeerUserId(peerUserId);
      }
      navigateTo(
        nextSnapshot(snapshotRef.current, {
          view: 'messages',
          authPane,
          assetsBrowseParams: { ...defaultAssetsBrowseParams },
        }),
      );
      setMessagesTabFocus((n) => n + 1);
    },
    [navigateTo, authPane],
  );

  const openSearch = useCallback(
    (initialKeyword?: string) => {
      const trimmed = initialKeyword?.trim() || null;
      navigateTo(
        nextSnapshot(snapshotRef.current, {
          view: 'search',
          authPane,
          assetsBrowseParams: { ...defaultAssetsBrowseParams },
          searchQuery: trimmed,
        }),
      );
    },
    [navigateTo, authPane],
  );

  const updateSearchQuery = useCallback(
    (query: string | null) => {
      const snapshot = snapshotRef.current;
      if (snapshot.view !== 'search') {
        return;
      }
      const trimmed = query?.trim() || null;
      if (snapshot.searchQuery === trimmed) {
        return;
      }
      navigateTo(
        nextSnapshot(snapshot, {
          view: 'search',
          searchQuery: trimmed,
        }),
        { replace: true },
      );
    },
    [navigateTo],
  );

  const openProfile = useCallback(() => {
    if (!isLoggedInNow()) {
      navigateTo(
        nextSnapshot(snapshotRef.current, {
          view: 'auth',
          authPane: 'login',
          assetsBrowseParams: { ...defaultAssetsBrowseParams },
          searchQuery: null,
        }),
      );
      return;
    }
    navigateTo(
      nextSnapshot(snapshotRef.current, {
        view: 'profile',
        authPane,
        assetsBrowseParams: { ...defaultAssetsBrowseParams },
      }),
    );
  }, [navigateTo, authPane]);

  /** Opens the profile tab with one hub row (e.g. `contact`) already expanded. */
  const openProfileHubRow = useCallback(
    (rowId: string) => {
      setProfileHubRowRequest((current) => ({ rowId, nonce: (current?.nonce ?? 0) + 1 }));
      openProfile();
    },
    [openProfile],
  );

  const registerBookingFlowGuard = useCallback((guard: BookingFlowGuard | null) => {
    bookingFlowGuardRef.current = guard ?? inactiveBookingFlowGuard;
  }, []);

  const requestBookingFlowLeave = useCallback((): boolean => {
    const guard = bookingFlowGuardRef.current;
    if (!guard.isActive) {
      return true;
    }
    if (!guard.confirmLeave()) {
      return false;
    }
    guard.abandonFlow();
    return true;
  }, []);

  const navigateFromBottomBar = useCallback(
    (target: AppView): boolean => {
      if (!requestBookingFlowLeave()) {
        return false;
      }
      const resolvedTarget =
        !isLoggedInNow() &&
        (target === 'profile' ||
          target === 'bookings' ||
          target === 'favorites' ||
          target === 'assets' ||
          target === 'messages')
          ? 'auth'
          : target;
      const resolvedAuthPane =
        resolvedTarget === 'auth' && !isLoggedInNow() ? 'login' : authPane;
      navigateTo(
        nextSnapshot(snapshotRef.current, {
          view: resolvedTarget,
          authPane: resolvedAuthPane,
          assetsBrowseParams:
            resolvedTarget === 'assets'
              ? { ...defaultAssetsBrowseParams }
              : browseParamsForView(resolvedTarget, assetsBrowseParams),
          searchQuery: resolvedTarget === 'search' ? snapshotRef.current.searchQuery : null,
        }),
      );
      switch (target) {
        case 'home':
          setHomeTabFocus((n) => n + 1);
          break;
        case 'assets':
          setAssetsTabFocus((n) => n + 1);
          break;
        case 'bookings':
          setBookingsTabFocus((n) => n + 1);
          break;
        case 'favorites':
          setFavoritesTabFocus((n) => n + 1);
          break;
        case 'messages':
          setMessagesTabFocus((n) => n + 1);
          break;
        case 'profile':
          setProfileTabFocus((n) => n + 1);
          break;
        default:
          break;
      }
      return true;
    },
    [requestBookingFlowLeave, navigateTo, authPane, assetsBrowseParams],
  );

  const openConnectionTest = useCallback(() => {
    navigateTo(
      nextSnapshot(snapshotRef.current, {
        view: 'connectionTest',
        authPane,
        assetsBrowseParams: { ...defaultAssetsBrowseParams },
      }),
    );
  }, [navigateTo, authPane]);

  const value = useMemo<AppNavigationValue>(
    () => ({
      view,
      setView,
      authPane,
      openAuth,
      assetsBrowseParams,
      bookingsBrowseParams,
      assetsTabFocus,
      bookingsTabFocus,
      favoritesTabFocus,
      messagesTabFocus,
      homeTabFocus,
      profileTabFocus,
      requestBookingFlowLeave,
      registerBookingFlowGuard,
      navigateFromBottomBar,
      openAssets,
      showAssetInBrowse,
      clearAssetsBrowseAssetId,
      clearAssetsBrowseReturnView,
      openBookings,
      clearBookingsBrowseBookingId,
      openFavorites,
      openMessages,
      openSearch,
      updateSearchQuery,
      searchQuery,
      openProfile,
      openProfileHubRow,
      profileHubRowRequest,
      openConnectionTest,
      navigationBack,
    }),
    [
      view,
      setView,
      authPane,
      openAuth,
      assetsBrowseParams,
      bookingsBrowseParams,
      assetsTabFocus,
      bookingsTabFocus,
      favoritesTabFocus,
      messagesTabFocus,
      homeTabFocus,
      profileTabFocus,
      requestBookingFlowLeave,
      registerBookingFlowGuard,
      navigateFromBottomBar,
      openAssets,
      showAssetInBrowse,
      clearAssetsBrowseAssetId,
      clearAssetsBrowseReturnView,
      openBookings,
      clearBookingsBrowseBookingId,
      openFavorites,
      openMessages,
      openSearch,
      updateSearchQuery,
      searchQuery,
      openProfile,
      openProfileHubRow,
      profileHubRowRequest,
      openConnectionTest,
      navigationBack,
    ],
  );

  return <AppNavigationContext.Provider value={value}>{children}</AppNavigationContext.Provider>;
}

export function useAppNavigation(): AppNavigationValue {
  const ctx = useContext(AppNavigationContext);
  if (!ctx) {
    throw new Error('useAppNavigation must be used within AppNavigationProvider');
  }
  return ctx;
}

export function useAppView(): AppView {
  return useAppNavigation().view;
}

export { createDefaultSnapshot };
